
import React from 'react';
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Variable, VariableCategory } from "@/types/variables";
import VariableCard from "./VariableCard";
import { Button } from "@/components/ui/button";

interface VariableListProps {
  variables: Variable[];
  onEdit: (variable: Variable) => void;
  onDelete: (id: string) => void;
  onAddNew: () => void;
}

const VariableList: React.FC<VariableListProps> = ({ variables, onEdit, onDelete, onAddNew }) => {
  const [searchTerm, setSearchTerm] = React.useState("");
  const [categoryFilter, setCategoryFilter] = React.useState<VariableCategory | "All">("All");

  const filteredVariables = variables.filter((variable) => {
    const matchesSearch = variable.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      variable.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
      variable.value.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesCategory = categoryFilter === "All" || variable.category === categoryFilter;
    
    return matchesSearch && matchesCategory;
  });

  const uniqueCategories = Array.from(new Set(variables.map((v) => v.category)));

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row gap-4">
        <div className="flex-1">
          <Input
            placeholder="Search variables..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="w-full md:w-64">
          <Select
            value={categoryFilter}
            onValueChange={(value) => setCategoryFilter(value as VariableCategory | "All")}
          >
            <SelectTrigger>
              <SelectValue placeholder="Filter by category" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="All">All Categories</SelectItem>
              {uniqueCategories.map((category) => (
                <SelectItem key={category} value={category}>
                  {category}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <Button onClick={onAddNew}>Add Variable</Button>
      </div>
      
      {filteredVariables.length === 0 ? (
        <div className="text-center py-12">
          <p className="text-muted-foreground">No variables found. Try adjusting your search or add a new variable.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredVariables.map((variable) => (
            <VariableCard
              key={variable.id}
              variable={variable}
              onEdit={onEdit}
              onDelete={onDelete}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default VariableList;
